import React, { useMemo, useRef } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  allMenuItemSx,
  ALL_LABEL,
  ALL_VALUE,
  badgeStyle,
  checkboxSx,
  formControlSx,
  inputLabelSx,
  menuPaperSx,
  optionMenuItemSx,
  placeholderTextStyle,
  selectSx,
  subTitleTypographySx,
  titleTypographySx,
  valueContainerStyle,
  valueTextStyle,
} from "./styles";
import { useDropdownSelectAll, useElementWidth } from "./hooks";
import { DropdownSelectAllProps } from "./types";

import { maskCNPJAlfa } from "@utils/fiscal/cnpj/cnpj.helpers";
// import { maskCNPJAlfa } from "@utils/fiscal/cnpj/cnpj.helpers";
import { capitalizeFirstLetterFullString } from "@utils/index";

const DropdownSelectAll: React.FC<DropdownSelectAllProps> = ({
  options,
  value,
  label,
  onChange,
  isDisabled = false,
  isCNPJ = false,
  isSubTitle = false,
  error = false,
  messageError = "",
  menuMaxHeight,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const width = useElementWidth(containerRef);

  const {
    selectedValues,
    allSelected,
    someSelected,
    handleChange,
    getRenderValue,
  } = useDropdownSelectAll(options, value, onChange);

  const visibleValue = useMemo(() => getRenderValue(width), [
    getRenderValue,
    width,
  ]);

  return (
    <FormControl
      ref={containerRef}
      fullWidth
      variant="outlined"
      error={error}
      sx={formControlSx(theme)}
    >
      <InputLabel shrink sx={inputLabelSx(theme)}>
        {label}
      </InputLabel>

      <Select
        multiple
        displayEmpty
        fullWidth
        disabled={isDisabled}
        value={selectedValues}
        onChange={handleChange}
        IconComponent={ExpandMoreIcon}
        renderValue={(selected) => {
          if (!(selected as string[])?.length) {
            return <span style={placeholderTextStyle(theme)}>Selecione</span>;
          }

          return (
            <span style={valueContainerStyle}>
              <span style={valueTextStyle}>{visibleValue.text}</span>
              {visibleValue.hiddenCount > 0 && (
                <span style={badgeStyle(theme)}>
                  +{visibleValue.hiddenCount}
                </span>
              )}
            </span>
          );
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              ...menuPaperSx(theme),
              ...(menuMaxHeight ? { maxHeight: menuMaxHeight } : {}),
            },
          },
          MenuListProps: {
            sx: { py: 0 },
          },
        }}
        sx={selectSx(theme)}
      >
        <MenuItem value={ALL_VALUE} sx={allMenuItemSx}>
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                disableRipple
                sx={checkboxSx(theme, isMobile)}
              />
            </Grid>

            <Grid item>
              <Typography sx={titleTypographySx(theme, isMobile)}>
                {ALL_LABEL}
              </Typography>
            </Grid>
          </Grid>
        </MenuItem>

        {options.map((option, index) => (
          <MenuItem
            key={`${option.value}-${index + 1}`}
            value={option.value}
            sx={optionMenuItemSx(isSubTitle)}
          >
            <Grid container wrap="nowrap">
              <Grid item>
                <Checkbox
                  checked={selectedValues.includes(option.value)}
                  disableRipple
                  sx={checkboxSx(theme, isMobile)}
                />
              </Grid>

              <Grid item>
                <Typography sx={titleTypographySx(theme, isMobile)}>
                  {capitalizeFirstLetterFullString(option.label)}
                </Typography>

                {isSubTitle && (
                  <Typography sx={subTitleTypographySx(theme)}>
                    {isCNPJ ? maskCNPJAlfa(option.value) : option.value}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </MenuItem>
        ))}
      </Select>

      <FormHelperText error={error}>{messageError}</FormHelperText>
    </FormControl>
  );
};

export default DropdownSelectAll;
